import IconClose from '@fortuneo/ui-icons/general/IconClose'
import Button, { type ButtonProps } from '@ui/atoms/button'
import { ClickableIcon } from '@ui/atoms/clickable-icon'
import { clsx } from 'clsx/lite'
import React, { type ReactNode } from 'react'

export type ModalProps = {
  buttons?: ButtonProps[]
  children?: ReactNode
  fullScreen?: boolean
  onClose?: (event: React.MouseEvent<HTMLElement>) => void
  modalTitle?: string | ReactNode
  visible?: boolean
} & React.HTMLAttributes<HTMLElement>

export const Modal = (props: ModalProps) => {
  const {
    children,
    buttons,
    fullScreen,
    modalTitle,
    onClose,
    visible,
    className,
    ...rest
  } = props

  return (
    <div
      role="dialog"
      className={clsx(
        'fixed bottom-0 left-0 right-0 top-0 z-30 flex h-dvh items-center justify-center',
        visible
          ? 'pointer-events-auto visible'
          : 'pointer-events-none invisible',
        className
      )}
      {...rest}
    >
      <div
        className={clsx(
          'bottom-0 z-[2] flex flex-col overflow-y-auto bg-background-default p-8',
          'tablet:relative tablet:bottom-auto',
          fullScreen
            ? 'top-0 h-full w-full'
            : 'h-auto w-full rounded-[5px] tablet:max-h-[80%] tablet:w-3/5 tablet:max-w-2xl',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-[20%] opacity-0'
        )}
      >
        <div className="mb-3.5 flex items-center gap-4">
          {typeof modalTitle === 'string' ? <h4>{modalTitle}</h4> : modalTitle}
          <div className="absolute right-0 top-0 z-[2] border-0 p-6 outline-none">
            <ClickableIcon
              aria-label="dialog-close-button"
              icon={IconClose}
              role="button"
              onClick={onClose}
            />
          </div>
        </div>
        {children && (
          <div
            className={clsx(
              'z-[1] flex-shrink overflow-y-auto overflow-x-hidden',
              fullScreen ? 'h-full' : 'h-auto'
            )}
          >
            {children}
          </div>
        )}
        {buttons && (
          <div className="mt-[22px] flex flex-col justify-center gap-5">
            {buttons.map((button, key) => {
              const { children, ...rest } = button
              return (
                <div key={key} className="flex justify-center">
                  <Button {...rest}>{children}</Button>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(
          'absolute bottom-0 left-0 right-0 top-0 z-[1] bg-black-50',
          visible && 'opacity-50'
        )}
        onClick={onClose}
      />
    </div>
  )
}
